import { apiErrorText } from "../../api/api-error.js";
import { usePortalValue } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Test chat"` and `data-screen-label="Test rail"` — the test panel and the
 * strip it collapses to.
 *
 * THE TEXT TEST, WHEREVER IT APPEARS (customer-portal review #91, #117). Typing a line gets the live
 * agent's own reply — the runtime assembles the same prompt a phone call uses and asks the same
 * model — so what it says here is what a caller would hear. It is free, and nothing the agent says
 * it will do is carried out: a line that would have placed an order says so in a `system` line.
 * The chat keeps its history (the agent sees what was said before) until **New chat**.
 *
 * AMENDED 2026-10-09 (design sync, `WeTalk.dc.html` "Test chat" and "Test console"): THREE MODES —
 * **Text**, **Browser voice** and the phone, labelled **My phone** for an agent that answers calls and
 * **Call me** for one that makes them (a survey). The phone test is no longer a form beside the panel
 * or a link to the agent's Test tab: it is the panel's third mode (`phone-test-call.ts`). The header
 * says what the chosen mode costs ("Free test" for text; "Tests are free." for the voice modes — plan-017 WAPP-B,
 * owner answers Q4 and PQ-14) with a dot in the accent. The panel has two layouts: `panel`, the fixed
 * side panel on the agent's page (with its close button), and `card`, the bounded card the Test console
 * (and the agent's Test tab) draws, with no close button. The Versions tab's draft panel offers Text
 * only (FR-BV-1 (c): a browser or phone test runs on the live version only).
 *
 * AMENDED 2026-10-09 (plan-015 TC1, G20, GC): the text hint names the live version's documents — "Type
 * what a caller would say. The agent replies from menu.pdf, prices.csv." (design 9754), read with
 * `listVoiceAgentLiveDocuments` — and says only its first sentence while they are unknown or there are
 * none. Under the voice modes' cost line, a delivery or booking agent's test adds its one real-effect
 * sentence (`testCallConsequenceOf`, owner answer 16). My phone renews and releases its arm by itself
 * (`phone-test-call.ts`, owner answer 18).
 *
 * AMENDED 2026-10-09 (plan-015 TC2, G21, G22, design 3947 / 9734–9755): a survey with two question
 * groups draws "Test group 1 · group 2" under the modes (`aria-label="Questionnaire to test"`), and every
 * mode sends the chosen group (`A` / `B`) — the api refuses a two-group survey's test without one, and
 * never substitutes. Choosing the other group starts a new text chat. A text test of an agent that makes
 * calls (a survey) opens with its greeting: the panel asks for the chat's opening (`openTestChat`) as
 * soon as it can run, so the agent's line is there before the tester types anything — turn 0 of the
 * stored transcript, free like every text test.
 *
 * AMENDED 2026-10-09 (plan-015 QSET-OFF, owner answer 36): a phone test of an agent whose number is on the account's
 * own SIP trunk goes through that trunk's own path, which carries no question group (the api refuses one there). So in
 * the phone mode such an agent is offered no group row — a short plain note instead ({@link OWN_TRUNK_GROUP_NOTE}) —
 * and "Call me" sends no group. Text and Browser voice keep the row. Accepted for now by the owner.
 *
 * What cannot run is said when the panel opens (review #166): the language model runs in scheduled
 * windows, and outside them the send button waits with the reason above it. After a send the box is
 * cleared at once; a refused line comes back into the box, with the reason in words (review #91).
 *
 * AMENDED 2026-10-19 (plan-021, design §33.8.12a, the owner's OD-17 decision for Release 1): the text test never passes a
 * conversation to another agent — it is the api's sandbox text test, which keeps no conversation between turns. For an
 * agent whose live version lists at least one pass target, its foot says so in one quiet line and points the tester to a
 * test call, which does pass ({@link voiceAgentPassTestChatNoteShown}). Any other agent shows nothing.
 *
 * AMENDED 2026-09-26 (plan-004 WC-10, FR-SC-1…3, FR-SC-6, FR-SC-7): the conversation scrolls. The
 * message area keeps the newest line in view while the tester is at the bottom, leaves the view
 * alone when they have scrolled up and offers "New messages" instead (`useStickToBottom`). The
 * message area is a polite live region, so a screen reader reads each reply; focus goes back to
 * the box after a send. At phone width the side panel follows the visual viewport, so the on-screen
 * keyboard does not cover the box.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button, Field, Icon, IconButton, Input, Select, Tag } from "@wetalk/ui";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { BrowserVoiceStatus, LevelMeter, useBrowserTestCall, useStartGate } from "./browser-voice-test.js";
import { elapsedSecondOf, isConnected, isUnderWay } from "./browser-voice-state.js";
import { endedLineOf } from "./ended-line.js";
import { NewestLineButton } from "./newest-line-button.js";
import { PaymentElementCard } from "./payment-element-card.js";
import { interleavedEntries } from "./payment-element.js";
import { isE164, usePhoneTestCall, type PhoneTestCallControl } from "./phone-test-call.js";
import { ConfirmationCodeCard } from "./confirmation-code-card.js";
import { useStickToBottom, useViewportBlockSize } from "./use-stick-to-bottom.js";
import { normalisedPhone, phoneExampleOf, unansweredOutcomeOf } from "./voice-test-form.js";
import { testCallConsequenceOf } from "./sandbox-disclosure.js";
import { isSurveyAgentTemplate } from "../agents/[id]/agent-tabs.js";
import { armCountOf } from "../campaigns/campaign-copy.js";
import { displayNumberOf } from "../numbers/number-display.js";
import { sipTrunkListQuery } from "../numbers/sip-trunk-query.js";
import { GLYPH } from "../../shell/glyph.js";
import { betaLanguageLabelOf } from "../../shell/language-beta.js";
import { useMemberCan } from "../../shell/member-capability.js";
import { useGateway } from "../../api/provider.js";
import { languagePackQuery, voiceAgentListQuery, voiceAgentQuery, voiceAgentQuestionnaireQuery } from "../../api/query.js";
import {
  voiceAgentNameOf,
  voiceAgentPassLiveTranscriptOf,
  voiceAgentPassTranscriptOf,
  type VoiceAgentNameOf,
  type VoiceAgentPassEventView,
  type VoiceAgentPassTranscriptEntry,
  type VoiceAgentStintView,
} from "../../api/voice-agent-stint-view.js";
import { VoiceAgentPassLine } from "./voice-agent-pass-line.js";
import { formatDuration } from "../../money.js";
import type { TestCallAvailabilityView, TestChatTurnView, TranscriptLineView, VoiceAgentView } from "../../api/gateway.js";
import { TestCallUnavailableError, type SurveyArmView } from "../../api/slice/test-call.js";
import { useVoiceAgentPassTargets, type VoiceAgentPassTargetsView } from "../../api/slice/voice-agent-pass.js";

import "./test-console.css";

/** The availability query every test surface shares, so the panel and the form never disagree. */
export const TEST_CALL_AVAILABILITY_KEY = ["testAvailability"] as const;

type PanelMode = "text" | "voice" | "phone";

/** Where the tester's own phone is remembered between visits (this browser only, a convenience). */
const MY_PHONE_STORAGE_KEY = "wetalk.testConsole.myPhone";

export interface TestChatPanelProps {
  readonly voiceAgentId: string;
  readonly voiceAgentName: string;
  /** The agent's languages (kept for the call sites; the phone test reads them from the agent). */
  readonly languageCode: readonly string[];
  /** The side panel's close button. The `card` layout has none. */
  readonly onClose?: () => void;
  /**
   * Kept for the agent page's call site: non-null offers the Browser voice and phone modes (unless
   * `voiceModes` says otherwise). The phone test runs in the panel itself now, so it is not called.
   */
  readonly onOpenTestCall: (() => void) | null;
  /** Whether Browser voice and the phone are offered beside Text. Defaults to `onOpenTestCall !== null`. */
  readonly voiceModes?: boolean;
  /** `panel`: the fixed side panel (default). `card`: the bounded card on the Test console. */
  readonly layout?: "panel" | "card";
  /** The agent, when the caller has it; otherwise it is read (the agent page has it cached). */
  readonly voiceAgent?: VoiceAgentView;
  /**
   * A built version to chat with instead of the live one — an edit tried on the Versions tab before
   * it is published (customer-portal review #75). Its chat is kept apart from the live one.
   */
  readonly agentVersionId?: string;
}

export function TestChatPanel(props: TestChatPanelProps): ReactNode {
  usePortalTranslation("operations");
  return <TestChatPanelConversation key={`${props.voiceAgentId}:${props.agentVersionId ?? "live"}`} {...props} />;
}

/**
 * "Free test" for text; "Tests are free." for a voice mode. Every test is free since the talk rate cutover (owner
 * answers Q4 and PQ-5: no trial seconds, no included minutes, no money), so no mode names a price (PQ-14).
 */
export function costLabelOf(mode: PanelMode): string {
  return mode === "text" ? portalText("operations", "free_test_52cd533e") : portalText("core", "talk_rate.tests_are_free");
}

/**
 * The text test's empty-chat hint (design 9754): a survey's tester answers; anyone else is told which
 * files the agent replies from, when they are known (plan-015 TC1, G20).
 */
export function textHintOf(survey: boolean, filename: readonly string[] | undefined): string {
  if (survey) return portalText("operations", "answer_as_the_person_called_would_2b90649b");
  if (filename === undefined || filename.length === 0) return portalText("operations", "type_what_a_caller_would_say_ed453d8a");
  return portalText("operations", "type_what_a_caller_would_say_the_agent_replies_from_value_92f68679", { v0: String(filename.join(", ")) });
}

/**
 * plan-021 (design §33.8.12a): whether the text test says that passing is tried only in a test call — when the agent's kind
 * passes conversations and its live version lists at least one pass target. Unread, refused or failed: nothing is said.
 */
export function voiceAgentPassTestChatNoteShown(view: VoiceAgentPassTargetsView | undefined): boolean {
  return view !== undefined && view.offered && view.liveVersion !== null && view.liveVersion.target.length > 0;
}

/** The two question groups of a survey, as the design labels them (design 9735). */
export const SURVEY_GROUP_OPTION: readonly { readonly value: SurveyArmView; readonly label: string }[] = [
  { value: "A", get label() { return portalText("operations", "test_group_1_583a6a2b"); } },
  { value: "B", get label() { return portalText("operations", "group_2_a88f2648"); } },
];

/** The phone mode's name: an agent that makes calls rings you; one that answers calls is rung. */
export function phoneModeLabelOf(agentTemplateCode: string | undefined): string {
  return agentTemplateCode !== undefined && isSurveyAgentTemplate(agentTemplateCode) ? portalText("operations", "call_me_a9ba81ad") : portalText("operations", "my_phone_cf363f87");
}

function TestChatPanelConversation({
  voiceAgentId,
  voiceAgentName,
  onClose,
  onOpenTestCall,
  voiceModes,
  layout = "panel",
  voiceAgent,
  agentVersionId,
}: TestChatPanelProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const availability = useQuery({
    queryKey: TEST_CALL_AVAILABILITY_KEY,
    queryFn: () => gateway.readTestCallAvailability(),
    staleTime: 30_000,
  });
  const offersVoice = voiceModes ?? onOpenTestCall !== null;
  // plan-015 TC2: read whenever the caller has not handed it over — a survey's text test opens with its
  // greeting and may need a question group, the Versions tab's text-only panel included.
  const voiceAgentRead = useQuery({ ...voiceAgentQuery(gateway, voiceAgentId), enabled: voiceAgent === undefined });
  const testedVoiceAgent = voiceAgent ?? voiceAgentRead.data;
  const [mode, setMode] = useState<PanelMode>("text");
  // A browser test or a phone call under way locks the mode choice (switching away would end it).
  const [locked, setLocked] = useState(false);
  const panelRef = useViewportBlockSize<HTMLElement>();
  const card = layout === "card";
  const survey = testedVoiceAgent !== undefined && isSurveyAgentTemplate(testedVoiceAgent.agentTemplateCode);
  // plan-015 TC2 (G21): a survey's live questionnaire says whether it has two question groups.
  const questionnaire = useQuery({ ...voiceAgentQuestionnaireQuery(gateway, voiceAgentId), enabled: survey });
  const twoGroups = survey && armCountOf(questionnaire.data?.question ?? []) > 1;
  const [group, setGroup] = useState<SurveyArmView>("A");
  const surveyArm = twoGroups ? group : undefined;
  // Until a survey's groups are known, its text test does not open: the opening must carry the group.
  const groupKnown = !survey || questionnaire.isSuccess || questionnaire.isError;
  // plan-015 QSET-OFF (owner answer 36): a phone test through the account's own SIP trunk cannot choose a group.
  const trunk = useQuery({ ...sipTrunkListQuery(gateway), enabled: twoGroups });
  const partyNumber = testedVoiceAgent?.partyNumber ?? null;
  const phoneOnOwnTrunk =
    twoGroups &&
    partyNumber !== null &&
    (trunk.data ?? []).some((one) => one.number.some((row) => row.voiceAgentId === voiceAgentId && row.e164 === partyNumber));
  const groupOffered = twoGroups && !(mode === "phone" && phoneOnOwnTrunk);

  const modeOption: readonly { readonly value: PanelMode; readonly label: string }[] = [
    { value: "text", label: portalText("operations", "text_c3328c39") },
    { value: "voice", label: portalText("operations", "browser_voice_4543e662") },
    { value: "phone", label: phoneModeLabelOf(testedVoiceAgent?.agentTemplateCode) },
  ];

  return (
    <aside
      ref={card ? undefined : panelRef}
      className={card ? "c-sandbox-panel c-sandbox-card" : "c-sandbox-panel"}
      aria-label={portalText("operations", "test_this_agent_b1d657d6")}
    >
      <div className="c-sandbox-head">
        <span className="c-sandbox-tag">
          <span className="c-sandbox-dot" aria-hidden="true" />
          {costLabelOf(mode)}
        </span>
        <span className="c-sandbox-name">{voiceAgentName}</span>
        {card || onClose === undefined ? null : <IconButton glyph={GLYPH.x} label={portalText("operations", "close_test_panel_079aa066")} onClick={onClose} />}
      </div>

      {offersVoice ? (
        <fieldset className="c-sandbox-mode" disabled={locked}>
          <div className="seg" role="radiogroup" aria-label={portalText("operations", "how_to_test_d9e9b9bc")}>
            {modeOption.map((option) => (
              <label key={option.value} className="seg-opt">
                <input
                  type="radio"
                  name={`sandboxMode-${voiceAgentId}-${layout}`}
                  value={option.value}
                  checked={option.value === mode}
                  onChange={() => {
                    setMode(option.value);
                  }}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}

      {twoGroups && !groupOffered ? (
        <div className="c-sandbox-mode c-sandbox-group">
          <p className="c-sandbox-hint" role="note">
            {OWN_TRUNK_GROUP_NOTE()}
          </p>
        </div>
      ) : null}

      {groupOffered ? (
        <fieldset className="c-sandbox-mode c-sandbox-group" disabled={locked}>
          <div className="seg" role="radiogroup" aria-label={portalText("operations", "questionnaire_to_test_611a3843")}>
            {SURVEY_GROUP_OPTION.map((option) => (
              <label key={option.value} className="seg-opt">
                <input
                  type="radio"
                  name={`sandboxGroup-${voiceAgentId}-${layout}`}
                  value={option.value}
                  checked={option.value === group}
                  onChange={() => {
                    if (option.value === group) return;
                    // A chat belongs to one group: the other group starts a new one (and its opening).
                    void gateway.startNewTestChat(voiceAgentId, agentVersionId).then(() => {
                      setGroup(option.value);
                    });
                  }}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}

      {mode === "voice" && offersVoice ? (
        <BrowserVoicePanelSection
          voiceAgentId={voiceAgentId}
          agentTemplateCode={testedVoiceAgent?.agentTemplateCode}
          availability={availability.data}
          onUnderWay={setLocked}
          surveyArm={surveyArm}
        />
      ) : mode === "phone" && offersVoice ? (
        testedVoiceAgent === undefined ? (
          <div className="c-sandbox-scroll">
            <div className="c-sandbox-body">
              <p className="c-sandbox-hint" role="status">
                {voiceAgentRead.isError ? portalText("operations", "the_agent_could_not_be_read_reload_the_page_then_try_again_192dba9b") : portalText("operations", "reading_the_agent_fbf1f951")}
              </p>
            </div>
          </div>
        ) : (
          <PhonePanelSection
            voiceAgent={testedVoiceAgent}
            availability={availability.data}
            onUnderWay={setLocked}
            surveyArm={phoneOnOwnTrunk ? undefined : surveyArm}
          />
        )
      ) : (
        <TextPanelSection
          // A chat belongs to one question group: choosing the other remounts it on a new chat.
          key={surveyArm ?? "no-group"}
          voiceAgentId={voiceAgentId}
          agentVersionId={agentVersionId}
          availability={availability.data}
          survey={survey}
          opens={survey && groupKnown}
          surveyArm={surveyArm}
          onRefetchAvailability={() => {
            void availability.refetch();
          }}
        />
      )}
    </aside>
  );
}

/**
 * plan-015 QSET-OFF (owner answer 36): what the phone mode says instead of the group row when the agent's number is on
 * the account's own SIP trunk.
 */
export const OWN_TRUNK_GROUP_NOTE =
  () => (portalText("operations", "this_number_comes_through_your_own_phone_system_so_a_phone_30a0a4e6"));

interface TextPanelSectionProps {
  readonly voiceAgentId: string;
  readonly agentVersionId: string | undefined;
  readonly availability: TestCallAvailabilityView | undefined;
  /** A survey's tester answers as the person called would. */
  readonly survey: boolean;
  /**
   * plan-015 TC2 (G22): the agent makes calls, so the chat opens with its greeting (turn 0) before any
   * input. False while a survey's question groups are still being read.
   */
  readonly opens: boolean;
  /** plan-015 TC2 (G21): a two-group survey's chosen group, sent with the opening and every line. */
  readonly surveyArm: SurveyArmView | undefined;
  readonly onRefetchAvailability: () => void;
}

function TextPanelSection({
  voiceAgentId,
  agentVersionId,
  availability,
  survey,
  opens,
  surveyArm,
  onRefetchAvailability,
}: TextPanelSectionProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const [draft, setDraft] = useState("");
  // The design draws no language choice for a text test: it starts in the agent's first language and
  // follows the tester if they type in another of its languages.
  const language = "";
  const [turn, setTurn] = useState<readonly TestChatTurnView[]>(() => gateway.testChatSnapshot(voiceAgentId, agentVersionId).turn);
  const [ended, setEnded] = useState(() => gateway.testChatSnapshot(voiceAgentId, agentVersionId).ended);
  const epoch = useRef(0);
  const [pending, setPending] = useState<string | null>(() => gateway.testChatSnapshot(voiceAgentId, agentVersionId).pending ?? null);
  const [message, setMessage] = usePortalValue<{ readonly text: string; readonly neutral: boolean } | null>(null);
  // plan-015 TC2 (G22): the chat's opening is on its way; which "New chat" this chat is.
  const [opening, setOpening] = useState(false);
  const [chatGeneration, setChatGeneration] = useState(0);

  useEffect(() => {
    const refresh = (): void => {
      const held = gateway.testChatSnapshot(voiceAgentId, agentVersionId, language);
      setTurn(held.turn);
      setEnded(held.ended);
      setPending(held.pending ?? null);
    };
    return gateway.subscribeTestChat(refresh);
  }, [gateway, voiceAgentId, agentVersionId, language]);

  const send = useMutation({
    mutationFn: (text: string) => gateway.sendTestChatTurn(voiceAgentId, text, language, agentVersionId, surveyArm),
    onMutate: (text: string) => {
      // Cleared the moment it is sent, and shown in the chat while the agent answers.
      setPending(text);
      setDraft("");
      setMessage(null);
      return epoch.current;
    },
    onSuccess: (next, _text, sentEpoch) => {
      if (sentEpoch !== epoch.current) return;
      setTurn(next);
      setEnded(gateway.testChatSnapshot(voiceAgentId, agentVersionId, language).ended);
      setPending(null);
    },
    onError: (error: unknown, text: string, sentEpoch) => {
      if (sentEpoch !== epoch.current) return;
      // Nothing was answered: the line goes back into the box so it can be sent again.
      setPending(null);
      setDraft(text);
      setMessage(() => ({
        get text() { return error instanceof Error ? apiErrorText(error, "") : portalText("operations", "that_line_could_not_be_sent_0afed8f3"); },
        neutral: error instanceof TestCallUnavailableError,
      }));
      onRefetchAvailability();
    },
  });

  // The box a caller's line is typed into: focus returns here after a send and after "New messages".
  const composer = useRef<HTMLFormElement>(null);
  const focusComposer = useCallback((): void => {
    composer.current?.querySelector("input")?.focus();
  }, []);

  // What the message area shows, as the hook counts it: the settled lines, the line being answered
  // and the refusal note. Its newest line's length changes when the reply replaces the pending line.
  const lineCount = turn.length + (pending === null ? 0 : 1) + (message === null ? 0 : 1);
  const lastLineLength = (message?.text ?? pending ?? turn.at(-1)?.text ?? "").length;
  const { bindLog, hasNewLine, jumpToNewest } = useStickToBottom<HTMLDivElement>({
    lineCount,
    lastLineLength,
    onJump: focusComposer,
  });

  const newChat = useMutation({
    mutationFn: () => {
      epoch.current += 1;
      setPending(null);
      setDraft("");
      setEnded(false);
      setOpening(false);
      return gateway.startNewTestChat(voiceAgentId, agentVersionId, language);
    },
    onSuccess: () => {
      setTurn([]);
      setMessage(null);
      setChatGeneration((one) => one + 1);
    },
  });

  const unavailable = availability !== undefined && !availability.sandboxTurnAvailable;

  // plan-015 TC2 (G22): an agent that makes calls speaks first. Once per chat, as soon as a text turn can
  // run, the chat's opening is asked for; the greeting arrives as turn 0.
  const openedFor = useRef<number | null>(null);
  const openChat = gateway.openTestChat;
  const canOpen = opens && availability?.sandboxTurnAvailable === true;
  useEffect(() => {
    if (!canOpen || turn.length > 0 || pending !== null || ended || openedFor.current === epoch.current) return;
    openedFor.current = epoch.current;
    const sentEpoch = epoch.current;
    setOpening(true);
    setMessage(null);
    openChat.call(gateway, voiceAgentId, agentVersionId, surveyArm).then(
      (next) => {
        if (sentEpoch !== epoch.current) return;
        setTurn(next);
        setOpening(false);
      },
      (error: unknown) => {
        if (sentEpoch !== epoch.current) return;
        setOpening(false);
        setMessage(() => ({
          get text() { return error instanceof Error ? apiErrorText(error, "") : portalText("operations", "the_agent_s_greeting_could_not_be_read_choose_new_chat_to_d152fa1d"); },
          neutral: error instanceof TestCallUnavailableError,
        }));
      },
    );
  }, [canOpen, openChat, gateway, voiceAgentId, agentVersionId, surveyArm, turn.length, pending, ended, chatGeneration, setMessage]);
  const placeholder = survey ? portalText("operations", "answer_as_the_person_called_would_04d178c9") : portalText("operations", "type_as_a_caller_would_speak_ea7a127b");
  // plan-015 TC1 (G20): the files the live version answers from, for the hint.
  const liveDocument = useQuery({
    queryKey: ["voiceAgentLiveDocument", voiceAgentId],
    queryFn: () => gateway.listVoiceAgentLiveDocuments(voiceAgentId),
    enabled: !survey,
    staleTime: 60_000,
  });
  // plan-021 (design §33.8.12a): the text test never passes; an agent with pass targets says so in the foot.
  const voiceAgentPassTarget = useVoiceAgentPassTargets(voiceAgentId);

  return (
    <>
      <div className="c-sandbox-scroll">
        <div ref={bindLog} className="c-sandbox-body" role="log" aria-live="polite" aria-label={portalText("operations", "test_conversation_6f666c81")}>
          {unavailable && typeof availability.message === "string" ? (
            <p className="c-note" role="status">
              {availability.message}
            </p>
          ) : null}
          {turn.length === 0 && pending === null && !opening ? (
            <p className="c-sandbox-hint">{textHintOf(survey, liveDocument.data?.map((one) => one.filename))}</p>
          ) : null}
          {opening ? (
            <span className="c-sandbox-thinking" role="status">
              {portalText("operations", "agent_is_speaking_57bddbec")}</span>
          ) : null}
          {turn.map((one, index) => (
            <ChatLine key={`${String(index)}:${one.text.slice(0, 16)}`} line={one} />
          ))}
          {pending === null ? null : <ChatLine line={{ speaker: "caller", text: pending }} />}
          {pending === null ? null : (
            <span className="c-sandbox-thinking" role="status">
              {portalText("operations", "agent_is_speaking_57bddbec")}</span>
          )}
          {ended ? <p className="c-sandbox-hint" role="status">{portalText("operations", "conversation_finished_choose_new_chat_to_test_again_7c95e145")}</p> : null}
          {message === null ? null : (
            <p className={message.neutral ? "c-note" : "c-note c-note-critical"} role="status">
              {message.text}
            </p>
          )}
        </div>
        {hasNewLine ? <NewestLineButton onJump={jumpToNewest} /> : null}
      </div>

      <div className="c-sandbox-foot">
        <form
          ref={composer}
          className="c-sandbox-send"
          onSubmit={(event) => {
            event.preventDefault();
            if (draft.trim().length === 0 || pending !== null || ended || opening) return;
            send.mutate(draft.trim());
            // FR-SC-7: a click on Send (which is then disabled) would otherwise drop focus.
            focusComposer();
          }}
        >
          <Input
            aria-label={portalText("operations", "what_a_caller_would_say_43cf304e")}
            placeholder={placeholder}
            value={draft}
            onChange={(event) => {
              setDraft(event.currentTarget.value);
            }}
          />
          <button
            type="submit"
            className="btn btn-primary btn-icon"
            aria-label={portalText("operations", "send_9bc2575c")}
            disabled={pending !== null || ended || unavailable || opening}
            title={unavailable ? (availability.message ?? undefined) : undefined}
          >
            <Icon glyph={GLYPH.paperPlaneRight} size="lg" />
          </button>
        </form>
        <p className="c-sandbox-note">
          {portalText("operations", "free_nothing_is_billed_for_text_tests_a150bce2")}{turn.length === 0 && pending === null && message === null ? null : (
            <>
              {" "}
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  newChat.mutate();
                }}
              >
                {portalText("operations", "new_chat_009bf6b9")}</button>
            </>
          )}
        </p>
        {voiceAgentPassTestChatNoteShown(voiceAgentPassTarget.data) ? (
          <p className="c-sandbox-note" role="note">
            {portalText("voiceAgentPassHistory", "test_chat.not_tried")}
          </p>
        ) : null}
      </div>
    </>
  );
}

interface BrowserVoicePanelSectionProps {
  readonly voiceAgentId: string;
  /** The agent's template, for the one real-effect sentence (plan-015 TC1, GC); unknown until read. */
  readonly agentTemplateCode: string | undefined;
  readonly availability: TestCallAvailabilityView | undefined;
  readonly onUnderWay: (underWay: boolean) => void;
  /** plan-015 TC2 (G21): a two-group survey's chosen group, sent with the start. */
  readonly surveyArm: SurveyArmView | undefined;
}

/**
 * The panel's Browser voice mode (plan-004 WC-51, FR-BV-1 (a)): the conversation as chat bubbles in the
 * scrolling area (the same stick-to-bottom rules as the text test), what is happening above the
 * prototype's control row — the microphone button, fourteen level bars and the duration — in the foot.
 * Mounted only while Browser voice is chosen, so the browser voice module is fetched only then.
 */
function BrowserVoicePanelSection({ voiceAgentId, agentTemplateCode, availability, onUnderWay, surveyArm }: BrowserVoicePanelSectionProps): ReactNode {
  usePortalTranslation("operations");
  const consequence = testCallConsequenceOf(agentTemplateCode);
  const control = useBrowserTestCall(voiceAgentId, surveyArm);
  const gate = useStartGate(availability, control.moduleState);
  const underWay = isUnderWay(control.phase);
  useEffect(() => {
    onUnderWay(underWay);
  }, [underWay, onUnderWay]);
  useEffect(
    () => () => {
      onUnderWay(false);
    },
    [onUnderWay],
  );
  const { bindLog, hasNewLine, jumpToNewest } = useStickToBottom<HTMLDivElement>({
    lineCount: control.line.length + control.payment.length,
    lastLineLength: control.line.at(-1)?.text.length ?? 0,
  });
  const { phase } = control;
  const interleaved = interleavedEntries(control.line, control.payment);
  const browserPassNameOf = useVoiceAgentPassNameOf([], control.voiceAgentPassArrival.map((one) => one.event));
  const browserPassEntry = voiceAgentPassLiveTranscriptOf({
    item: interleaved,
    lineOf: (entry) => (entry.kind === "line" ? entry.line : null),
    arrival: control.voiceAgentPassArrival,
    viewerVoiceAgentId: voiceAgentId,
    nameOf: browserPassNameOf,
    languageText: null,
  });
  const isLive = phase.kind === "live";
  const again = phase.kind === "ended" || phase.kind === "failed";
  const retry = phase.kind === "refused" && phase.retry;
  const between =
    phase.kind === "requesting_microphone"
      ? portalText("operations", "allow_the_microphone_in_your_browser_s_prompt_6c22c54e")
      : phase.kind === "arming" || phase.kind === "connecting"
        ? portalText("operations", "connecting_to_the_agent_e6d9ad69")
        : phase.kind === "queued"
          ? portalText("operations", "on_hold_all_of_the_agent_s_lines_are_busy_it_answers_when_5ae85d3f")
          : phase.kind === "reconnecting"
            ? portalText("operations", "reconnecting_the_connection_dropped_for_a_moment_6c723ebf")
            : null;

  return (
    <>
      <div className="c-sandbox-scroll">
        <div ref={bindLog} className="c-sandbox-body" role="log" aria-live="polite" aria-label={portalText("operations", "browser_test_conversation_c315c55a")}>
          {control.line.length === 0 && control.payment.length === 0 ? (
            <p className="c-sandbox-hint">
              {portalText("operations", "start_the_voice_test_and_speak_into_your_microphone_the_ag_70b907e4")}</p>
          ) : (
            // Plan-005 OP-37: a payment element the runtime published is drawn where it arrived.
            // plan-021: a passed test draws its dividers and names each agent line by its VoiceAgent.
            (browserPassEntry ?? interleaved.map((item): VoiceAgentPassTranscriptEntry<(typeof interleaved)[number]> => ({ kind: "item", item, voiceAgentName: null }))).map(
              (passEntry) => {
                if (passEntry.kind !== "item") return <VoiceAgentPassLine key={passEntry.key} entry={passEntry} />;
                const entry = passEntry.item;
                return entry.kind === "payment" ? (
                  <PaymentElementCard key={`payment:${entry.payment.customerOrderPaymentId}`} element={entry.payment} />
                ) : (
                  <ChatLine key={`${String(entry.index)}:${entry.line.text.slice(0, 16)}`} line={entry.line} who={passEntry.voiceAgentName} />
                );
              },
            )
          )}
        </div>
        {hasNewLine ? <NewestLineButton onJump={jumpToNewest} /> : null}
      </div>

      <div className="c-sandbox-foot">
        {/* plan-029 WAPP-HIST2: this test's confirmation code, shown instead of sent (the tester's eyes only). */}
        <ConfirmationCodeCard code={control.consoleCode} />
        <BrowserVoiceStatus control={control} gate={gate} />
        {between === null ? null : (
          <p className="c-note" role="status">
            {between}
          </p>
        )}
        {gate.roleReason !== null ? null : (
          <div className="tc-voice-row">
            {underWay ? (
              <Button
                variant="secondary"
                // Nothing to leave until the module holds the conversation.
                disabled={!isConnected(phase)}
                onClick={control.hangUp}
              >
                <Icon glyph={GLYPH.stopCircle} size="lg" />
                {portalText("operations", "end_test_3346e37b")}</Button>
            ) : (
              <Button
                variant="primary"
                disabled={control.moduleState !== "ready" || gate.unavailableReason !== null || gate.waitReason !== null}
                onClick={control.start}
              >
                <Icon glyph={GLYPH.microphone} size="lg" />
                {retry ? portalText("operations", "try_again_042c862e") : again ? portalText("operations", "test_again_2c64589b") : portalText("operations", "start_voice_test_f0ae88be")}
              </Button>
            )}
            <LevelMeter level={control.level} active={isLive} />
            <span className="tc-voice-duration" role={isLive ? "timer" : undefined}>
              {formatDuration(phase.kind === "live" ? elapsedSecondOf(phase.sinceMs, control.nowMs) : 0)}
            </span>
          </div>
        )}
        <p className="c-sandbox-note">{portalText("operations", "test_console.uses_your_microphone", { v0: portalText("core", "talk_rate.tests_are_free") })}</p>
        {consequence === null ? null : <p className="c-sandbox-note">{consequence}</p>}
      </div>
    </>
  );
}

/** The tester's phone remembered in this browser, or "" (storage can be unavailable). */
function rememberedPhone(): string {
  try {
    return globalThis.localStorage.getItem(MY_PHONE_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function rememberPhone(value: string): void {
  try {
    globalThis.localStorage.setItem(MY_PHONE_STORAGE_KEY, value);
  } catch {
    // A private window or blocked storage: the field simply starts empty next time.
  }
}

interface PhonePanelSectionProps {
  readonly voiceAgent: VoiceAgentView;
  readonly availability: TestCallAvailabilityView | undefined;
  readonly onUnderWay: (underWay: boolean) => void;
  /** plan-015 TC2 (G21): a two-group survey's chosen group, sent with "Call me". */
  readonly surveyArm: SurveyArmView | undefined;
}

/** How long typing must pause before a number is armed. */
const ARM_PAUSE_MS = 700;

/**
 * The panel's phone mode: **My phone** (the tester rings the agent) for an agent that answers calls,
 * **Call me** (the agent rings the tester and runs the survey) for one that makes them.
 */
function PhonePanelSection({ voiceAgent, availability, onUnderWay, surveyArm }: PhonePanelSectionProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const languagePack = useQuery(languagePackQuery(gateway));
  const member = useMemberCan();
  const control = usePhoneTestCall(voiceAgent);
  const outbound = isSurveyAgentTemplate(voiceAgent.agentTemplateCode);
  const consequence = testCallConsequenceOf(voiceAgent.agentTemplateCode);
  const [phone, setPhone] = useState(rememberedPhone);
  const [callLanguage, setCallLanguage] = useState(voiceAgent.languageCode[0] ?? "");
  const [checked, setChecked] = useState<string | null>(null);
  const underWay = control.state === "live" || control.state === "dialing";
  useEffect(() => {
    onUnderWay(underWay);
  }, [underWay, onUnderWay]);
  useEffect(
    () => () => {
      onUnderWay(false);
    },
    [onUnderWay],
  );
  const { bindLog, hasNewLine, jumpToNewest } = useStickToBottom<HTMLDivElement>({
    lineCount: control.line.length,
    lastLineLength: control.line.at(-1)?.text.length ?? 0,
  });

  const dialNumber = voiceAgent.partyNumber;
  const direction = outbound ? "agent_calls_me" : "i_call_the_agent";
  const roleReason = member.whyNot("test_call.place");
  const platformBlocked = availability !== undefined && !availability.directionAvailable.includes(direction);
  const countryCallingCode = availability?.countryCallingCode ?? null;
  const languageName = (code: string): string => languagePack.data?.find((one) => one.code === code)?.name ?? code;

  // My phone: arm the tester's number by itself — once it reads as a number, again after the window
  // passes unused (while the page is in view) and after a test call ends. A refused arm is not retried
  // until the number changes.
  const [visibleTick, setVisibleTick] = useState(0);
  useEffect(() => {
    const onVisible = (): void => {
      if (globalThis.document.visibilityState === "visible") setVisibleTick((tick) => tick + 1);
    };
    globalThis.document.addEventListener("visibilitychange", onVisible);
    return () => {
      globalThis.document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
  const normalised = normalisedPhone(phone);
  const canArm =
    !outbound &&
    dialNumber !== null &&
    roleReason === null &&
    availability !== undefined &&
    !platformBlocked &&
    isE164(phone) &&
    control.refusedFor !== normalised &&
    !control.pending &&
    control.state !== "live" &&
    control.state !== "dialing" &&
    !(control.state === "waiting" && control.partyNumber === normalised);
  // `control.start` is a new function every render; the arm depends only on what `canArm` reads.
  const startArm = useRef(control.start);
  useEffect(() => {
    startArm.current = control.start;
  });
  useEffect(() => {
    if (!canArm || globalThis.document.visibilityState !== "visible") return;
    const timer = setTimeout(() => {
      startArm.current(normalised, "i_call_the_agent", "");
    }, ARM_PAUSE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [canArm, normalised, visibleTick]);

  const ringMe = (): void => {
    if (underWay) {
      control.hangUp();
      return;
    }
    if (!isE164(phone)) {
      setChecked(
        portalText("operations", "enter_your_own_phone_number_with_the_country_code_for_exam_40649f14", { v0: String(phoneExampleOf(countryCallingCode)) }),
      );
      return;
    }
    setChecked(null);
    control.start(phone, "agent_calls_me", callLanguage, surveyArm);
  };

  const view = control.view;
  // plan-021 (design §33.13.3): the passes of this test — heard live, or from the read when its lines are the read's.
  const readPass = view !== null && control.voiceAgentPassArrival.length === 0 && control.line === view.transcript;
  const passNameOf = useVoiceAgentPassNameOf(
    readPass ? (view.voiceAgentStint ?? []) : [],
    readPass ? (view.voiceAgentPass ?? []) : control.voiceAgentPassArrival.map((one) => one.event),
  );
  const phonePassEntry = readPass
    ? voiceAgentPassTranscriptOf({
        item: control.line,
        lineOf: (line) => line,
        stint: view.voiceAgentStint ?? [],
        event: view.voiceAgentPass ?? [],
        viewerVoiceAgentId: voiceAgent.voiceAgentId,
        nameOf: passNameOf,
        languageText: null,
      })
    : voiceAgentPassLiveTranscriptOf({
        item: control.line,
        lineOf: (line) => line,
        arrival: control.voiceAgentPassArrival,
        viewerVoiceAgentId: voiceAgent.voiceAgentId,
        nameOf: passNameOf,
        languageText: null,
      });
  const unanswered =
    control.state === "ended" && view !== null && control.direction === "agent_calls_me" && view.billedSeconds === 0
      ? unansweredOutcomeOf(view.endReason)
      : null;
  const endedView = control.state === "ended" ? view : control.lastEnded;
  const elapsed = control.state === "live" ? formatDuration(view?.elapsedSeconds ?? 0) : null;

  const phoneField = (
    <Field label={portalText("operations", "your_phone_8b1c7089")}>
      {(field) => (
        <Input
          {...field}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder={phoneExampleOf(countryCallingCode)}
          value={phone}
          onChange={(event) => {
            const next = event.currentTarget.value;
            setPhone(next);
            setChecked(null);
            if (isE164(next)) rememberPhone(next);
          }}
        />
      )}
    </Field>
  );

  return (
    <>
      <div className="c-sandbox-scroll">
        <div ref={bindLog} className="c-sandbox-body" role="log" aria-live="polite" aria-label={portalText("operations", "phone_test_conversation_fd7d3ddb")}>
          {control.line.length === 0 ? (
            <p className="c-sandbox-hint">
              {outbound
                ? portalText("operations", "press_call_me_and_the_agent_rings_your_phone_the_conversat_d069640a")
                : portalText("operations", "call_the_number_below_from_your_phone_the_conversation_app_16a1bee3")}
            </p>
          ) : (
            // plan-021: a passed test draws its dividers and names each agent line by its VoiceAgent.
            (phonePassEntry ?? control.line.map((item): VoiceAgentPassTranscriptEntry<TranscriptLineView> => ({ kind: "item", item, voiceAgentName: null }))).map(
              (entry, index) =>
                entry.kind === "item" ? (
                  <ChatLine key={`${String(index)}:${entry.item.text.slice(0, 16)}`} line={transcriptTurnOf(entry.item)} who={entry.voiceAgentName} />
                ) : (
                  <VoiceAgentPassLine key={entry.key} entry={entry} />
                ),
            )
          )}
          {endedView !== null && unanswered === null && endedView.state === "ended" ? (
            <p className="c-sandbox-hint tc-ended" role="status">
              <Tag tone="neutral">{portalText("operations", "test_a94a8fe5")}</Tag>
              {endedLineOf(endedView)}
            </p>
          ) : null}
          {unanswered === null ? null : (
            <p className="c-sandbox-hint" role="status">
              {unanswered}
            </p>
          )}
        </div>
        {hasNewLine ? <NewestLineButton onJump={jumpToNewest} /> : null}
      </div>

      <div className="c-sandbox-foot">
        {/* plan-029 WAPP-HIST2: this call's confirmation code, shown instead of sent (the tester's eyes only). */}
        <ConfirmationCodeCard code={control.consoleCode} />
        <PhoneStatus control={control} platformBlocked={platformBlocked} availability={availability} roleReason={roleReason} />
        {dialNumber === null ? (
          <p className="c-note" role="status">
            {voiceAgent.displayName} {portalText("operations", "has_no_number_yet_so_b04dc594")}{outbound ? portalText("operations", "it_has_no_number_to_call_you_from_41d62fad") : portalText("operations", "there_is_nothing_to_call_94da0fcd")}.{" "}
            <Link to="/numbers">{portalText("operations", "give_it_one_in_numbers_sip_8c1478f2")}</Link>{portalText("operations", "then_test_it_7ffee960")}</p>
        ) : roleReason !== null ? null : outbound ? (
          <div className="c-sandbox-phone">
            {phoneField}
            <Field label={portalText("operations", "language_89b86ab0")}>
              {(field) => (
                <span className="tc-select">
                  <Select
                    {...field}
                    value={callLanguage}
                    onChange={(event) => {
                      setCallLanguage(event.currentTarget.value);
                    }}
                  >
                    {voiceAgent.languageCode.map((code) => (
                      <option key={code} value={code}>
                        {betaLanguageLabelOf(languagePack.data, code, languageName(code))}
                      </option>
                    ))}
                  </Select>
                  <Icon glyph={GLYPH.caretDown} size="xs" />
                </span>
              )}
            </Field>
            <Button
              variant={underWay ? "secondary" : "primary"}
              disabled={
                (underWay && control.conversationId === null) ||
                (!underWay && (control.pending || platformBlocked || availability === undefined))
              }
              onClick={ringMe}
            >
              <Icon glyph={underWay ? GLYPH.phoneDisconnect : GLYPH.phoneCall} size="md" />
              <span>
                {control.state === "dialing"
                  ? portalText("operations", "ringing_your_phone_hang_up_d42fb803")
                  : elapsed !== null
                    ? portalText("operations", "value_hang_up_fb7978bc", { v0: String(elapsed) })
                    : portalText("operations", "call_me_a9ba81ad")}
              </span>
            </Button>
            {checked === null ? null : <p className="c-note c-note-critical">{checked}</p>}
          </div>
        ) : (
          <>
            <p className="c-sandbox-lead">{portalText("operations", "call_the_agent_from_any_phone_bc45257d")}</p>
            <div className="c-sandbox-number">
              <span className="c-sandbox-number-value">{displayNumberOf(dialNumber)}</span>
              <CopyButton text={displayNumberOf(dialNumber)} />
            </div>
            {phoneField}
          </>
        )}
        <p className="c-sandbox-note">
          {outbound
            ? portalText("operations", "test_console.the_agent_rings_you_and_runs_the_survey", { v0: portalText("core", "talk_rate.tests_are_free") })
            : portalText("operations", "test_console.call_from_this_phone", { v0: portalText("core", "talk_rate.tests_are_free") })}
        </p>
        {consequence === null ? null : <p className="c-sandbox-note">{consequence}</p>}
      </div>
    </>
  );
}

function PhoneStatus({
  control,
  platformBlocked,
  availability,
  roleReason,
}: {
  readonly control: PhoneTestCallControl;
  readonly platformBlocked: boolean;
  readonly availability: TestCallAvailabilityView | undefined;
  readonly roleReason: string | null;
}): ReactNode {
  usePortalTranslation("operations");
  return (
    <>
      {roleReason === null ? null : (
        <p className="c-note" role="status">
          {roleReason} {portalText("operations", "you_can_read_what_a_phone_test_says_here_but_not_start_one_3225aeea")}</p>
      )}
      {platformBlocked ? (
        <div className="tc-unavailable" role="status">
          <span className="tc-unavailable-title">{portalText("operations", "voice_tests_cannot_run_right_now_8b648fe2")}</span>
          <p className="tc-unavailable-dek">
            {availability?.message ?? portalText("operations", "the_platform_cannot_take_a_test_call_right_now_try_again_l_4dd37b46")}
          </p>
        </div>
      ) : null}
      {control.state === "live" && control.direction === "i_call_the_agent" ? (
        <p className="c-note tc-live" role="status">
          <span className="c-dot" aria-hidden="true" />
          {formatDuration(control.view?.elapsedSeconds ?? 0)}
        </p>
      ) : null}
      {control.message === null ? null : <p className="c-note c-note-critical">{control.message}</p>}
      {control.unavailable === null ? null : (
        <div className="tc-unavailable" role="status">
          <span className="tc-unavailable-title">{portalText("operations", "the_test_cannot_run_right_now_42a23ed1")}</span>
          <p className="tc-unavailable-dek">{control.unavailable}</p>
        </div>
      )}
      {control.expired && control.state === "idle" && control.direction === "agent_calls_me" ? (
        <p className="c-note" role="status">
          {portalText("operations", "the_call_to_your_phone_did_not_go_through_nothing_was_char_51187188")}<strong>{portalText("operations", "call_me_a9ba81ad")}</strong> {portalText("operations", "to_try_once_more_530dbaee")}</p>
      ) : null}
    </>
  );
}

function CopyButton({ text }: { readonly text: string }): ReactNode {
  usePortalTranslation("operations");
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => {
      setCopied(false);
    }, 2_000);
    return () => {
      clearTimeout(timer);
    };
  }, [copied]);
  return (
    <Button
      variant="secondary"
      onClick={() => {
        void globalThis.navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
        });
      }}
    >
      {copied ? portalText("operations", "copied_8e3df45a") : portalText("operations", "copy_af74f7c5")}
    </Button>
  );
}

/** A transcript line as the chat draws it (the phone test streams `TranscriptLineView`s). */
function transcriptTurnOf(line: TranscriptLineView): TestChatTurnView {
  return { speaker: line.speaker === "assistant" ? "assistant" : line.speaker === "caller" ? "caller" : "system", text: line.text };
}

/**
 * plan-021: how a test's passes name their VoiceAgents — the test's stints, then the workspace's VoiceAgents (read only
 * once a pass names one).
 */
function useVoiceAgentPassNameOf(
  stint: readonly VoiceAgentStintView[],
  event: readonly VoiceAgentPassEventView[],
): VoiceAgentNameOf {
  const gateway = useGateway();
  const known = useQuery({ ...voiceAgentListQuery(gateway), enabled: event.length > 0 });
  return voiceAgentNameOf(
    stint,
    (known.data ?? []).map((one) => ({ voiceAgentId: one.voiceAgentId, displayName: one.displayName })),
  );
}

/** `who` (plan-021): the VoiceAgent that said an agent line of a passed test; `null` keeps "Agent". */
function ChatLine({ line, who = null }: { readonly line: TestChatTurnView; readonly who?: string | null }): ReactNode {
  usePortalTranslation("operations");
  if (line.speaker === "system") {
    return <p className="c-sandbox-hint">{line.text}</p>;
  }
  const caller = line.speaker === "caller";
  return (
    <div className={caller ? "c-chat-turn c-chat-turn-caller" : "c-chat-turn"}>
      <span className="c-chat-who">{caller ? portalText("operations", "you_905cb326") : (who ?? portalText("operations", "agent_5ce2e6f4"))}</span>
      <span className={caller ? "c-chat-bubble c-chat-bubble-caller" : "c-chat-bubble"}>{line.text}</span>
    </div>
  );
}

export interface SandboxRailProps {
  readonly onOpen: () => void;
  /** Shows the dot: this agent has been tested in this visit. */
  readonly hasHistory: boolean;
}

/** The collapsed state of the panel. One button and a vertical label. */
export function SandboxRail({ onOpen, hasHistory }: SandboxRailProps): ReactNode {
  usePortalTranslation("operations");
  return (
    <aside className="c-sandbox-rail" aria-label={portalText("operations", "test_this_agent_b1d657d6")}>
      <IconButton
        glyph={GLYPH.chatCircleDots}
        label={portalText("operations", "open_the_test_panel_87ee9956")}
        size="xl"
        title={portalText("operations", "test_this_agent_b1d657d6")}
        onClick={onOpen}
      />
      <span className="c-sandbox-rail-label">{portalText("operations", "test_agent_39afc1aa")}</span>
      {hasHistory ? (
        <span className="c-sandbox-dot c-sandbox-dot-history" title={portalText("operations", "there_is_a_test_in_progress_f204d52e")} />
      ) : null}
    </aside>
  );
}
